import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Network, Users, Search, ExternalLink, Loader, BookOpen, PlayCircle } from 'lucide-react';
import api from '../services/api';
import { demoService } from '../services/api';
import { brandGhostBtn, brandIconAccent, brandIconTile, brandPrimaryBtnMd } from '../components/brandChrome';
import { graphAlgorithmEngineLabel } from '../utils/graphAlgorithmStatus';

interface BetweennessResult {
    address: string;
    chain?: string;
    score: number;
    entity_name?: string;
}

interface PageRankResult {
    address: string;
    chain?: string;
    score: number;
    entity_name?: string;
}

interface CommunityResult {
    community_id: number;
    addresses: Array<{ address: string; chain: string; entity_name?: string }>;
    size: number;
}

export const Analytics = () => {
    const navigate = useNavigate();

    const inferChain = (address?: string): string => {
        if (!address) return '—';
        const a = address.trim();
        if (a.startsWith('0x')) return 'ETH/EVM';
        if (a.startsWith('T')) return 'TRX';
        if (a.startsWith('bc1') || a.startsWith('1') || a.startsWith('3')) return 'BTC';
        return '—';
    };

    const normalizeRankResults = <T extends { address: string; chain?: string }>(rows: T[]): T[] => {
        return (rows || []).map((row) => ({
            ...row,
            chain: row.chain || inferChain(row.address),
        }));
    };

    const normalizeCommunityResults = (rows: any[]): CommunityResult[] => {
        if (!Array.isArray(rows)) return [];
        return rows.map((community: any, idx: number) => {
            // Supports both frontend-shape {community_id, addresses[]} and backend-shape {communityId, members[]}
            if (Array.isArray(community?.addresses)) {
                return {
                    community_id: Number(community.community_id ?? idx + 1),
                    addresses: community.addresses.map((a: any) => ({
                        address: a.address,
                        chain: a.chain || inferChain(a.address),
                        entity_name: a.entity_name,
                    })),
                    size: Number(community.size ?? community.addresses.length ?? 0),
                };
            }

            const members: string[] = Array.isArray(community?.members) ? community.members : [];
            const parsedId = typeof community?.communityId === 'string' && community.communityId.startsWith('cluster_')
                ? Number(community.communityId.replace('cluster_', ''))
                : Number(community?.communityId ?? idx + 1);

            return {
                community_id: Number.isFinite(parsedId) ? parsedId : idx + 1,
                addresses: members.map((address: string) => ({
                    address,
                    chain: inferChain(address),
                })),
                size: Number(community?.size ?? members.length ?? 0),
            };
        });
    };
    
    // Betweenness state
    const [betweennessLoading, setBetweennessLoading] = useState(false);
    const [betweennessResults, setBetweennessResults] = useState<BetweennessResult[]>([]);
    const [betweennessEngine, setBetweennessEngine] = useState('');
    const [betweennessFocus, setBetweennessFocus] = useState('');
    const [betweennessLimit, setBetweennessLimit] = useState(20);
    
    // PageRank state
    const [pagerankLoading, setPagerankLoading] = useState(false);
    const [pagerankResults, setPagerankResults] = useState<PageRankResult[]>([]);
    const [pagerankEngine, setPagerankEngine] = useState('');
    const [pagerankFocus, setPagerankFocus] = useState('');
    const [pagerankLimit, setPagerankLimit] = useState(20);
    
    // Community state
    const [communityLoading, setCommunityLoading] = useState(false);
    const [communityResults, setCommunityResults] = useState<CommunityResult[]>([]);
    const [communityEngine, setCommunityEngine] = useState('');
    const [communityLimit, setCommunityLimit] = useState(10);
    const [cachedDemoIds, setCachedDemoIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        demoService.listCached()
            .then((list) => {
                const ids = Array.isArray(list)
                    ? list.map((item: any) => item.demo_id).filter(Boolean)
                    : [];
                setCachedDemoIds(new Set(ids));
            })
            .catch(() => setCachedDemoIds(new Set()));
    }, []);

    const openAlgorithmDemo = (mode: 'mock' | 'cached' | 'live' = 'mock') => {
        const base = '/workbench?demo=graph-algorithms-demo';
        navigate(mode === 'mock' ? base : `${base}&demo_mode=${mode}`);
    };

    const graphAlgoModeLabel = `Mock: available · Cached: ${cachedDemoIds.has('graph-algorithms-demo') ? 'ready' : 'not ready'} · Live: available`;

    const runBetweenness = async () => {
        setBetweennessLoading(true);
        try {
            const params: any = { limit: betweennessLimit };
            if (betweennessFocus.trim()) params.focus_address = betweennessFocus.trim();
            const { data } = await api.get('/addresses/graph/betweenness', { params });
            setBetweennessResults(normalizeRankResults(data.results || []));
            setBetweennessEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('Betweenness failed:', error);
        } finally {
            setBetweennessLoading(false);
        }
    };

    const runPageRank = async () => {
        setPagerankLoading(true);
        try {
            const params: any = { limit: pagerankLimit };
            if (pagerankFocus.trim()) params.focus_address = pagerankFocus.trim();
            const { data } = await api.get('/addresses/graph/pagerank', { params });
            setPagerankResults(normalizeRankResults(data.results || []));
            setPagerankEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('PageRank failed:', error);
        } finally {
            setPagerankLoading(false);
        }
    };

    const runCommunityDetection = async () => {
        setCommunityLoading(true);
        try {
            const { data } = await api.get('/addresses/graph/communities', { params: { limit: communityLimit } });
            setCommunityResults(normalizeCommunityResults(data.communities || []));
            setCommunityEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('Community detection failed:', error);
        } finally {
            setCommunityLoading(false);
        }
    };

    const runBetweennessDefaults = async () => {
        const defaultLimit = 20;
        setBetweennessFocus('');
        setBetweennessLimit(defaultLimit);
        setBetweennessLoading(true);
        try {
            const { data } = await api.get('/addresses/graph/betweenness', { params: { limit: defaultLimit } });
            setBetweennessResults(normalizeRankResults(data.results || []));
            setBetweennessEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('Betweenness defaults failed:', error);
        } finally {
            setBetweennessLoading(false);
        }
    };

    const runPageRankDefaults = async () => {
        const defaultLimit = 20;
        setPagerankFocus('');
        setPagerankLimit(defaultLimit);
        setPagerankLoading(true);
        try {
            const { data } = await api.get('/addresses/graph/pagerank', { params: { limit: defaultLimit } });
            setPagerankResults(normalizeRankResults(data.results || []));
            setPagerankEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('PageRank defaults failed:', error);
        } finally {
            setPagerankLoading(false);
        }
    };

    const runCommunityDefaults = async () => {
        const defaultLimit = 10;
        setCommunityLimit(defaultLimit);
        setCommunityLoading(true);
        try {
            const { data } = await api.get('/addresses/graph/communities', { params: { limit: defaultLimit } });
            setCommunityResults(normalizeCommunityResults(data.communities || []));
            setCommunityEngine(graphAlgorithmEngineLabel(data));
        } catch (error) {
            console.error('Community defaults failed:', error);
        } finally {
            setCommunityLoading(false);
        }
    };

    const openInWorkbench = (address: string, chain?: string) => {
        navigate(`/workbench?address=${address}&chain=${chain || 'ETH'}`);
    };

    return (
        <div className={`p-6 max-w-7xl mx-auto`}>
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                    <Network className="w-8 h-8" />
                    Graph Analytics
                </h1>
                <p className="text-muted-foreground">
                    Advanced Neo4j graph algorithms for network analysis and community detection
                </p>
                <div className="mt-3 p-3 border border-border rounded-lg bg-secondary/20 text-sm text-muted-foreground">
                    <div className="font-medium text-foreground mb-1 flex items-center gap-2"><BookOpen className="w-4 h-4" /> How to use this page</div>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Run each algorithm with default limits first, then narrow with focus addresses for case-specific deep dives.</li>
                        <li>Use <span className="font-medium text-foreground">Investigate</span> to pivot top-ranked addresses into Workbench for transaction-level evidence.</li>
                        <li>Demo scenario available: <span className="font-medium text-foreground">graph-algorithms-demo</span> ({cachedDemoIds.has('graph-algorithms-demo') ? 'cached ready' : 'cached unavailable, use mock/live'}).</li>
                    </ul>
                </div>
            </div>

            <div className="space-y-8">
                {/* Betweenness Centrality */}
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-primary/10 rounded-lg">
                            <BarChart3 className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold">Betweenness Centrality</h2>
                            <p className="text-sm text-muted-foreground">Find bridge addresses connecting different communities</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="md:col-span-2">
                            <label className="text-xs text-muted-foreground block mb-1">Focus Address (optional)</label>
                            <input
                                type="text"
                                value={betweennessFocus}
                                onChange={(e) => setBetweennessFocus(e.target.value)}
                                placeholder="0x... or T... (leave empty for global analysis)"
                                className="w-full px-3 py-2 bg-background border border-input rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Result Limit</label>
                            <input
                                type="number"
                                value={betweennessLimit}
                                onChange={(e) => setBetweennessLimit(parseInt(e.target.value) || 20)}
                                min={5}
                                max={100}
                                className="w-full px-3 py-2 bg-background border border-input rounded text-sm"
                            />
                        </div>
                    </div>

                    <button
                        onClick={runBetweenness}
                        disabled={betweennessLoading}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:bg-brand-gold-hover disabled:opacity-50 flex items-center gap-2"
                    >
                        {betweennessLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        {betweennessLoading ? 'Analyzing...' : 'Run Analysis'}
                    </button>

                    {betweennessEngine && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            Engine: <span className="font-medium text-foreground">{betweennessEngine}</span>
                        </div>
                    )}

                    <div className="mt-3 p-2 border border-border rounded bg-muted/20 text-xs text-muted-foreground">
                        <div className="font-medium text-foreground mb-1">Demo Guide — Betweenness</div>
                        <p>Use global run to find bridge wallets first. Then set a focus address to inspect local bridge behavior around suspect entities.</p>
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <button onClick={() => openAlgorithmDemo('mock')} className="px-2 py-1 rounded border border-border hover:bg-secondary inline-flex items-center gap-1"><PlayCircle className="w-3 h-3" /> Demo (Mock)</button>
                            <button onClick={() => openAlgorithmDemo(cachedDemoIds.has('graph-algorithms-demo') ? 'cached' : 'live')} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Demo ({cachedDemoIds.has('graph-algorithms-demo') ? 'Cached' : 'Live'})
                            </button>
                            <button onClick={runBetweennessDefaults} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Run demo defaults
                            </button>
                        </div>
                        <div className="mt-2 text-[11px]">Data modes: {graphAlgoModeLabel}. Live may be slower and noisier depending on API latency.</div>
                    </div>

                    {betweennessResults.length > 0 && (
                        <div className="mt-6 border border-border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-secondary/50 border-b border-border">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Rank</th>
                                        <th className="px-4 py-3 text-left">Address</th>
                                        <th className="px-4 py-3 text-left">Chain</th>
                                        <th className="px-4 py-3 text-left">Entity</th>
                                        <th className="px-4 py-3 text-right">Score</th>
                                        <th className="px-4 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {betweennessResults.map((result, idx) => (
                                        <tr key={idx} className="hover:bg-secondary/30">
                                            <td className="px-4 py-3 font-semibold text-primary">#{idx + 1}</td>
                                            <td className="px-4 py-3 font-mono text-xs">{result.address.substring(0, 12)}...{result.address.substring(result.address.length - 8)}</td>
                                            <td className="px-4 py-3">{result.chain}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{result.entity_name || '—'}</td>
                                            <td className="px-4 py-3 text-right font-medium">{result.score.toFixed(4)}</td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => openInWorkbench(result.address, result.chain)}
                                                    className="px-2 py-1 text-xs bg-primary/20 text-primary rounded hover:bg-primary/30 inline-flex items-center gap-1"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    Investigate
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* PageRank */}
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className={brandIconTile}>
                            <Network className={`w-6 h-6 ${brandIconAccent}`} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold">PageRank</h2>
                            <p className="text-sm text-muted-foreground">Identify influential addresses based on transaction flow</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="md:col-span-2">
                            <label className="text-xs text-muted-foreground block mb-1">Focus Address (optional)</label>
                            <input
                                type="text"
                                value={pagerankFocus}
                                onChange={(e) => setPagerankFocus(e.target.value)}
                                placeholder="0x... or T... (leave empty for global analysis)"
                                className="w-full px-3 py-2 bg-background border border-input rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground block mb-1">Result Limit</label>
                            <input
                                type="number"
                                value={pagerankLimit}
                                onChange={(e) => setPagerankLimit(parseInt(e.target.value) || 20)}
                                min={5}
                                max={100}
                                className="w-full px-3 py-2 bg-background border border-input rounded text-sm"
                            />
                        </div>
                    </div>

                    <button
                        onClick={runPageRank}
                        disabled={pagerankLoading}
                        className={`${brandPrimaryBtnMd} rounded disabled:opacity-50 flex items-center gap-2`}
                    >
                        {pagerankLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        {pagerankLoading ? 'Analyzing...' : 'Run Analysis'}
                    </button>

                    {pagerankEngine && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            Engine: <span className="font-medium text-foreground">{pagerankEngine}</span>
                        </div>
                    )}

                    <div className="mt-3 p-2 border border-border rounded bg-muted/20 text-xs text-muted-foreground">
                        <div className="font-medium text-foreground mb-1">Demo Guide — PageRank</div>
                        <p>PageRank highlights hubs where flows converge. High rank + high risk score often indicates major laundering or off-ramp infrastructure.</p>
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <button onClick={() => openAlgorithmDemo('mock')} className="px-2 py-1 rounded border border-border hover:bg-secondary inline-flex items-center gap-1"><PlayCircle className="w-3 h-3" /> Demo (Mock)</button>
                            <button onClick={() => openAlgorithmDemo(cachedDemoIds.has('graph-algorithms-demo') ? 'cached' : 'live')} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Demo ({cachedDemoIds.has('graph-algorithms-demo') ? 'Cached' : 'Live'})
                            </button>
                            <button onClick={runPageRankDefaults} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Run demo defaults
                            </button>
                        </div>
                        <div className="mt-2 text-[11px]">Data modes: {graphAlgoModeLabel}. Live may be slower and noisier depending on API latency.</div>
                    </div>

                    {pagerankResults.length > 0 && (
                        <div className="mt-6 border border-border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-secondary/50 border-b border-border">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Rank</th>
                                        <th className="px-4 py-3 text-left">Address</th>
                                        <th className="px-4 py-3 text-left">Chain</th>
                                        <th className="px-4 py-3 text-left">Entity</th>
                                        <th className="px-4 py-3 text-right">Score</th>
                                        <th className="px-4 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {pagerankResults.map((result, idx) => (
                                        <tr key={idx} className="hover:bg-secondary/30">
                                            <td className={`px-4 py-3 font-semibold ${brandIconAccent}`}>#{idx + 1}</td>
                                            <td className="px-4 py-3 font-mono text-xs">{result.address.substring(0, 12)}...{result.address.substring(result.address.length - 8)}</td>
                                            <td className="px-4 py-3">{result.chain}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{result.entity_name || '—'}</td>
                                            <td className="px-4 py-3 text-right font-medium">{result.score.toFixed(6)}</td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => openInWorkbench(result.address, result.chain)}
                                                    className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1 ${brandGhostBtn}`}
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    Investigate
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Community Detection */}
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-green-500/10 rounded-lg">
                            <Users className="w-6 h-6 text-green-500" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold">Community Detection</h2>
                            <p className="text-sm text-muted-foreground">Discover clusters of closely connected addresses</p>
                        </div>
                    </div>

                    <div className="flex items-end gap-4 mb-4">
                        <div className="flex-1">
                            <label className="text-xs text-muted-foreground block mb-1">Max Communities</label>
                            <input
                                type="number"
                                value={communityLimit}
                                onChange={(e) => setCommunityLimit(parseInt(e.target.value) || 10)}
                                min={3}
                                max={50}
                                className="w-full px-3 py-2 bg-background border border-input rounded text-sm"
                            />
                        </div>
                        <button
                            onClick={runCommunityDetection}
                            disabled={communityLoading}
                            className="px-4 py-2 bg-green-600 text-foreground rounded text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {communityLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            {communityLoading ? 'Analyzing...' : 'Detect Communities'}
                        </button>
                    </div>

                    {communityEngine && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            Engine: <span className="font-medium text-foreground">{communityEngine}</span>
                        </div>
                    )}

                    <div className="mt-1 p-2 border border-border rounded bg-muted/20 text-xs text-muted-foreground">
                        <div className="font-medium text-foreground mb-1">Demo Guide — Community Detection</div>
                        <p>Communities identify tightly interacting clusters. Use cluster outputs to prioritize ring investigations and common-controller hypotheses.</p>
                        <div className="flex gap-2 mt-2 flex-wrap">
                            <button onClick={() => openAlgorithmDemo('mock')} className="px-2 py-1 rounded border border-border hover:bg-secondary inline-flex items-center gap-1"><PlayCircle className="w-3 h-3" /> Demo (Mock)</button>
                            <button onClick={() => openAlgorithmDemo(cachedDemoIds.has('graph-algorithms-demo') ? 'cached' : 'live')} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Demo ({cachedDemoIds.has('graph-algorithms-demo') ? 'Cached' : 'Live'})
                            </button>
                            <button onClick={runCommunityDefaults} className="px-2 py-1 rounded border border-border hover:bg-secondary">
                                Run demo defaults
                            </button>
                        </div>
                        <div className="mt-2 text-[11px]">Data modes: {graphAlgoModeLabel}. Live may be slower and noisier depending on API latency.</div>
                    </div>

                    {communityResults.length > 0 && (
                        <div className="mt-6 space-y-4">
                            {communityResults.map((community) => (
                                <div key={community.community_id} className="border border-border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-semibold text-green-400">Community #{community.community_id}</h3>
                                        <span className="text-xs text-muted-foreground">{community.size} addresses</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {community.addresses.slice(0, 10).map((addr, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => openInWorkbench(addr.address, addr.chain)}
                                                className="px-2 py-1 bg-secondary/50 hover:bg-secondary rounded text-xs font-mono flex items-center gap-1"
                                            >
                                                {addr.address.substring(0, 8)}...
                                                <ExternalLink className="w-3 h-3" />
                                            </button>
                                        ))}
                                        {community.size > 10 && (
                                            <span className="px-2 py-1 text-xs text-muted-foreground">+{community.size - 10} more</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
